import { AxiosError, AxiosPromise, AxiosResponse } from 'axios'
import { Delete } from './Delete'
import { Filter } from './Devices'
import { Get } from './Get'
import { UserProfileObject } from './organization.types'
import { Post } from './Post'
import { Put } from './Put'
import { UserItemData } from './UserService.d'
import { ApiHelpers, APIHosts } from './ApiHelpers'

export interface UpdateInfoParams {
  companyName: string
  billingAddress: string
  billingAddress2: string
  billingCity: string
  billingState: string
  billingZip: string
  crmId?: string
  duns?: string
  naics?: string
  guid?: string
  code?: string
  taxCode?: string
  note?: string
  companyId?: number | string
  phoneNumber?: string
}
interface UpdateRoleParams {
  roleName: string
  newRoleName: string
}
interface UserFilter {
  filter?: string
  expand?: string
  paging?: boolean
  orderBy?: string
  page?: number
  pageSize?: number
  offset?: number
  take?: number
  invitationStatus?: string
  roles?: string
}

export interface OrgRoleItemResponse {
  id: string
  name: string
  normalizedName: string
  organizationId: string
  allowRemoveUser?: boolean
  users?: UserItemData[]
  allowEdit?: boolean
}

export interface RemoveRoleFromUsers {
  id: string
  organizationRoles: OrgRoleItemResponse
  roleId: string
  roleName: string | null
  userOrganizationId: string
}

export interface OnboardOrganizationParams {
  organization: UpdateInfoParams
  adminEmail: string
}

export interface OnboardOrganizationResponse {
  organizationId: string
  userId: string
  userOrgId: string
}
class OrganizationServiceController extends ApiHelpers {
  ApiHost = APIHosts.LicenseManager

  public Put = {
    updateOrganizationsInfo: (data: UpdateInfoParams) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Put(`${baseUrl}/organizations`, data)
    },
    updateOrganizationInfo: (id: string, data: Partial<UpdateInfoParams>) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${id}`
      return Put(url, data)
    },
    updateOrganizationRole: (id: string, data: UpdateRoleParams) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${id}/organizationRoles`
      return Put(url, data)
    },
  }

  public Get = {
    getOrganizationUsers: async (
      orgId: string,
      data?: UserFilter,
      roles?: string[],
      invitationStatus?: string[],
    ) => {
      const baseUrl = this.getHost(this.ApiHost)
      let url = `${baseUrl}/organizations/${orgId}/users`
      let rolesParam = ''
      if (roles) {
        rolesParam = `roles=${roles.join('&roles=')}`
      }
      let statusParam = ''
      if (invitationStatus) {
        statusParam = `invitationStatus=${invitationStatus.join(
          '&invitationStatus=',
        )}`
      }
      if (rolesParam || statusParam) {
        url += `?${[rolesParam, statusParam].filter(i => i).join('&')}`
      }

      const filterData = {
        params: {
          ...data,
        },
      }
      return Get(url, filterData)
    },
    searchOrganizationUsers: (data: UserFilter) => {
      const filterData = {
        params: {
          ...data,
        },
      }
      const baseUrl = this.getHost(this.ApiHost)
      return Get(`${baseUrl}/organizations`, filterData)
    },
    getOrganization: (orgId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}`
      return Get(url)
    },
    getOrganizationLicense: (orgId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}/licenses`
      return Get(url)
    },
    getOrganizationRoles: (
      orgId: string,
    ): AxiosPromise<OrgRoleItemResponse[] | AxiosError> => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}/organizationRoles`
      return Get(url)
    },
    getOrganizationAdmin: (orgId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}/administrators`
      return Get(url)
    },
    getUsersToInvite: async (data: Filter) => {
      const baseUrl = this.getHost(this.ApiHost)
      const filterData = { params: { ...data } }
      return Get(`${baseUrl}/users`, filterData)
    },
  }

  public Post = {
    addOrganizationAdmin: (orgId: string, adminId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}/administrators`
      return Post(url, [adminId], {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    addOrganizationUser: (orgId: string, userIds: string[]) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}/users`
      return Post(url, userIds, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    createOrganizations: (data: UpdateInfoParams) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/organizations`, data)
    },
    createOrganizationRole: (orgId: string, newRoles: string[]) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}/organizationRoles`
      return Post(url, newRoles)
    },
    changeUserStatus: (
      orgId: string,
      data: { email: string; status: string }[],
    ) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/organizations/${orgId}/notify/userinvites`, data)
    },
    addOrgLicenseToUsers: (userId: string, licenseIds: string[]) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(
        `${baseUrl}/organizations/users/${userId}/licenses`,
        licenseIds,
      )
    },
    addOrganizationRolesToUser: (
      userId: string,
      roleData: { organizationId: string; roleName: string }[],
    ) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/users/${userId}/organizationRoles`, roleData)
    },
    addUserToOrganization: (data: UserProfileObject) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/organizations/users/register`, data)
    },

    createOrganization: (data: UpdateInfoParams[]) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/organizations`, data)
    },

    onboardOrganization: (
      data: OnboardOrganizationParams,
    ): AxiosPromise<AxiosResponse<OnboardOrganizationResponse> | AxiosError> => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/organizations/onboard`, data)
    },

    exportSelectedStaffs: (orgId: string, userIds: string[]) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(
        `${baseUrl}/organizations/${orgId}/users/export-selected`,
        {
          ids: userIds,
        },
        {
          responseType: 'blob',
        },
      )
    },
    exportAllStaffsInOrganization: (orgId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(
        `${baseUrl}/organizations/${orgId}/users/export-all`,
        {},
        {
          responseType: 'blob',
        },
      )
    },
    exportStaffsInCurrentPage: (orgId: string, data: any) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(
        `${baseUrl}/organizations/${orgId}/users/export-current-page`,
        {},
        {
          params: {
            ...data,
          },
          responseType: 'blob',
        },
      )
    },
  }

  public Delete = {
    removeUsers: (orgId: string, userIds: string[]) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}/users`
      return Delete(url, userIds)
    },
    removeUsersFromOrgLicense: (
      userId: string,
      licenseIds: string[],
    ): AxiosPromise<AxiosResponse<number> | AxiosError> => {
      const baseUrl = this.getHost(this.ApiHost)
      return Delete(
        `${baseUrl}/organizations/users/${userId}/licenses`,
        licenseIds,
      )
    },
    removeOrganizationRolesFromUser: (
      userId: string,
      roleData: { organizationId: string; roleName: string }[],
    ): AxiosPromise<AxiosResponse<RemoveRoleFromUsers[]> | AxiosError> => {
      const baseUrl = this.getHost(this.ApiHost)
      return Delete(`${baseUrl}/users/${userId}/organizationRoles`, roleData)
    },
    removeRoleFromOrganization: (
      organizationId: string,
      roles: string[],
    ): AxiosPromise<AxiosResponse<OrgRoleItemResponse> | AxiosError> => {
      const baseUrl = this.getHost(this.ApiHost)
      return Delete(
        `${baseUrl}/organizations/${organizationId}/organizationRoles`,
        roles,
      )
    },
  }
}

export const OrganizationsService = new OrganizationServiceController()
